import React from 'react'
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  Fade,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'

interface PaymentProcessingModalProps {
  open: boolean
  status?: string | null
  mode?: 'redirecting' | 'processing'
}

export const PaymentProcessingModal: React.FC<PaymentProcessingModalProps> = ({
  open,
  status,
  mode,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const currentMode = mode || (status ? 'processing' : 'redirecting')
  const isRedirecting = currentMode === 'redirecting'

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: isMobile ? '16px' : '20px',
          overflow: 'hidden',
          boxShadow: '0 24px 48px -12px rgba(15,23,42,0.18), 0 0 0 1px rgba(15,23,42,0.06)',
          m: isMobile ? 1.5 : 3,
          width: isMobile ? 'calc(100% - 24px)' : '100%',
          maxWidth: isMobile ? '100%' : 520,
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px) saturate(0.6)',
          bgcolor: 'rgba(255, 255, 255, 0.1)',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>

        {/* Top gradient hero zone */}
        <Box
          sx={{
            background: 'linear-gradient(90deg, #3F66BB 0%, #34A1FB 100%)',
            px: isMobile ? 3 : 4,
            pt: isMobile ? 3 : 4,
            pb: isMobile ? 3 : 3.5,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Rotating purple radial accent — matches UpdateKnowledgeDialog header */}
          <Box sx={{
            position: 'absolute', top: '-50%', left: '-10%', width: '50%', height: '200%',
            background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, rgba(0,0,0,0) 70%)',
            transform: 'rotate(30deg)',
            pointerEvents: 'none',
          }} />

          {/* Animated ring icon */}
          <Box sx={{ position: 'relative', display: 'inline-flex', mb: isMobile ? 2 : 2.5 }}>
            <Box sx={{
              width: isMobile ? 56 : 64,
              height: isMobile ? 56 : 64,
              borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Box sx={{
                width: isMobile ? 38 : 44,
                height: isMobile ? 38 : 44,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CheckCircleOutlineRoundedIcon sx={{ fontSize: isMobile ? 20 : 24, color: '#fff' }} />
              </Box>
            </Box>
            {/* Spinning arc */}
            <Box sx={{
              position: 'absolute',
              top: -4, left: -4,
              width: isMobile ? 64 : 72,
              height: isMobile ? 64 : 72,
              borderRadius: '50%',
              border: '2.5px solid transparent',
              borderTopColor: 'rgba(255,255,255,0.9)',
              borderRightColor: 'rgba(255,255,255,0.2)',
              animation: 'spin 1s linear infinite',
              '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
            }} />
          </Box>

          <Typography
            variant={isMobile ? 'subtitle1' : 'h6'}
            fontWeight={800}
            sx={{ color: '#fff', letterSpacing: '-0.01em', mb: 0.75 }}
          >
            {isRedirecting ? 'Redirecting...' : 'Processing Payment'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
            {isRedirecting
              ? 'Please wait while we launch your secure payment window.'
              : (status || 'Please hold on while we confirm your transaction.')}
          </Typography>
        </Box>

        {/* Bottom info zone */}
        <Box sx={{ px: isMobile ? 2.5 : 3.5, py: isMobile ? 2.5 : 3, bgcolor: '#fff' }}>

          {/* Info notice */}
          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1.5,
            p: isMobile ? 1.75 : 2,
            borderRadius: '12px',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.5)' : '#F8FAFC',
            border: '1px solid',
            borderColor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
            mb: 2.5,
          }}>
            <InfoOutlinedIcon sx={{ fontSize: 19, color: '#2563EB', flexShrink: 0, mt: 0.15 }} />
            <Box>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  display: 'block',
                  color: (theme) => (theme.palette.mode === 'dark' ? '#F1F5F9' : '#0F172A'),
                  fontSize: '0.8125rem',
                  lineHeight: 1.4,
                  mb: 0.25,
                }}
              >
                {isRedirecting ? 'Taking you to checkout...' : 'Do not refresh or close this tab'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : '#64748B'),
                  fontSize: '0.75rem',
                  lineHeight: 1.45,
                  display: 'block',
                }}
              >
                {isRedirecting
                  ? 'Your payment window will pop up automatically in a moment.'
                  : 'Plan activation may take 1–2 minutes after payment completion.'}
              </Typography>
            </Box>
          </Box>

          {/* Security footer */}
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.6} sx={{ color: '#94a3b8' }}>
            <LockIcon sx={{ fontSize: 12 }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.3px' }}>
              256-Bit SSL Encrypted
            </Typography>
          </Stack>

        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default PaymentProcessingModal
